import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'

export type CoeDashboardAccess = {
  /** False while Split is still initialising (treatment is `control`). */
  isResolved: boolean
  /** The user may open /coe-dashboard (V2_SWITCH or V2_ONLY). */
  canUseV2: boolean
  /** The user may move between V1 and V2 in both directions (V2_SWITCH). */
  canSwitch: boolean
}

/**
 * Resolves the COE_DASHBOARD rollout switch for the signed-in user.
 *
 * Read through the *user* Split client (keyed by lowercased email) so that
 * per-email targeting in Harness resolves the same way on every surface that
 * calls this: the /coe-dashboard layout, the legacy support navbar door, the
 * legacy patient-card door and the V2 "Classic view" link.
 *
 * Fail-closed: anything that is not a recognised V2 treatment — including
 * `control` while the client initialises, or when the split is missing in an
 * environment — resolves to no V2 access. Treatment names are compared
 * case-insensitively so the Split can be defined as `V2_SWITCH` or
 * `v2_switch`.
 */
export const resolveCoeDashboardAccess = (
  treatment: string | null | undefined
): CoeDashboardAccess => {
  const value = (treatment ?? '').toUpperCase()
  const isResolved =
    value !== '' && value !== treatmentFlagValues.CONTROL.toUpperCase()
  const canSwitch =
    value === treatmentFlagValues.COE_DASHBOARD_V2_SWITCH.toUpperCase()
  const canUseV2 =
    canSwitch ||
    value === treatmentFlagValues.COE_DASHBOARD_V2_ONLY.toUpperCase()

  return { isResolved, canUseV2, canSwitch }
}

const useCoeDashboardTreatment = (): CoeDashboardAccess =>
  resolveCoeDashboardAccess(useGetUserTreatment(TreatmentFlags.COE_DASHBOARD))

export default useCoeDashboardTreatment
